import { useEffect, useState, type ReactNode } from "react";
import styled from "styled-components";
import { Link, NavLink, useNavigate, type NavLinkProps } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import type { ChainId, ResellerId, StoreId } from "@KassaroAB/shared/types/ids";
import type { RestStore } from "@KassaroAB/shared/types/store";
import { UserRole } from "@KassaroAB/shared/types/enums/UserRole";
import { useGetChainQuery } from "../query/chainsQuery";
import { useGetResellerQuery } from "../query/resellersQuery";
import { useGetChainStoresQuery, useGetStoreQuery, useGetStoresQuery } from "../query/storesQuery";
import { useGetUserQuery } from "../query/usersQuery";
import { DropdownArrow } from "./Navbar";

export const SidebarContainer = styled.aside`
  width: 240px;
  background-color: ${colors.background};
  border-right: 1px solid ${colors.border};
  padding: ${spacing.x2}px 0 calc(${spacing.x2}px + env(safe-area-inset-bottom, 0px));
  height: 100%;
  overflow-y: auto;
  @media (max-width: 768px) {
    width: 280px;
    display: flex;
    flex-direction: column;
    padding-top: 0;
    overflow: hidden;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  }
`;

export const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  @media (max-width: 768px) {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }
`;

export const NavItem = styled.li`
  margin: 0;
`;

export const StyledNavLink = styled(NavLink)`
  display: block;
  padding: ${spacing.x2}px ${spacing.x3}px;
  color: ${colors.text};
  text-decoration: none;
  font-size: ${fontSizes.regular}px;
  transition: background-color 0.2s;
  min-height: 44px;
  display: flex;
  align-items: center;

  &:hover {
    background-color: ${colors.primary};
    color: ${colors.primaryContrastText};
  }

  &.active {
    background-color: ${colors.primary};
    color: ${colors.primaryContrastText};
    font-weight: 600;
  }

  @media (max-width: 768px) {
    padding: ${spacing.x1}px ${spacing.x2}px;
    min-height: 40px;
  }
`;

export const NavSection = styled.div`
  margin-bottom: ${spacing.x2}px;
`;

export const NavSectionHeader = styled.div`
  padding: ${spacing.x2}px ${spacing.x3}px ${spacing.x1}px ${spacing.x3}px;
  margin-bottom: ${spacing.x1}px;
`;

export const NavSectionTitle = styled.div`
  font-size: ${fontSizes.small}px;
  font-weight: 600;
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${spacing.x05}px;
`;

export const NavSubtitle = styled.div`
  font-size: ${fontSizes.regular}px;
  font-weight: 600;
  color: ${colors.text};
  line-height: 1.4;
`;

const NavGroupToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.x1}px;
  width: 100%;
  padding: ${spacing.x2}px ${spacing.x3}px;
  min-height: 44px;
  border: none;
  background: none;
  color: ${colors.text};
  font-size: ${fontSizes.regular}px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.primary};
    color: ${colors.primaryContrastText};
  }

  @media (max-width: 768px) {
    padding: ${spacing.x1}px ${spacing.x2}px;
    min-height: 40px;
  }
`;

const NavGroupLabelWrap = styled.span`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

const NavGroupChevron = styled(ChevronDown)<{ $open: boolean }>`
  flex-shrink: 0;
  transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
`;

const NavGroupItems = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 0 ${spacing.x1}px;
  background-color: ${colors.backgroundDark};
`;

const NavGroupItem = styled.li`
  margin: 0;
`;

const NavGroupLinkAnchor = styled(StyledNavLink)`
  padding: ${spacing.x1}px ${spacing.x3}px ${spacing.x1}px ${spacing.x3 + spacing.x1}px;
  min-height: 32px;

  @media (max-width: 768px) {
    padding: ${spacing.x1}px ${spacing.x2}px ${spacing.x1}px ${spacing.x3}px;
    min-height: 36px;
  }
`;

type NavGroupProps = {
  label: ReactNode;
  open: boolean;
  onToggle: () => void;
  children?: ReactNode;
};

export const NavGroup = ({ label, open, onToggle, children }: NavGroupProps) => {
  return (
    <>
      <NavGroupToggle type="button" aria-expanded={open} onClick={onToggle}>
        <NavGroupLabelWrap>{label}</NavGroupLabelWrap>
        <NavGroupChevron size={16} $open={open} />
      </NavGroupToggle>
      {open && <NavGroupItems>{children}</NavGroupItems>}
    </>
  );
};

export const NavGroupLink = (props: NavLinkProps) => {
  return (
    <NavGroupItem>
      <NavGroupLinkAnchor {...props} />
    </NavGroupItem>
  );
};

const ContextHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
  padding: ${spacing.x2}px ${spacing.x3}px ${spacing.x2}px;

  @media (max-width: 768px) {
    gap: ${spacing.x1}px;
    padding: ${spacing.x1}px ${spacing.x2}px;
  }
`;

const ContextLabel = styled.div`
  font-size: ${fontSizes.small}px;
  font-weight: 600;
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${spacing.x05}px;
`;

const ContextName = styled.div`
  font-size: ${fontSizes.regular}px;
  font-weight: 600;
  color: ${colors.text};
  line-height: 1.4;
`;

const ContextLink = styled(Link)`
  display: block;
  font-size: ${fontSizes.regular}px;
  font-weight: 600;
  color: ${colors.text};
  line-height: 1.4;
  text-decoration: none;

  &:hover {
    color: ${colors.primary};
  }
`;

const StoreMenu = styled.div`
  position: relative;
`;

const StoreMenuTrigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.x1}px;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  color: ${colors.text};
  font-size: ${fontSizes.regular}px;
  font-weight: 600;
  line-height: 1.4;
  text-align: left;
  cursor: pointer;

  &:hover {
    color: ${colors.primary};
  }

  @media (max-width: 768px) {
    min-height: 36px;
    padding: ${spacing.x05}px ${spacing.x1}px;
    border-radius: ${borderRadius.small}px;
    background-color: ${colors.backgroundDark};
  }
`;

const StoreMenuList = styled.div`
  position: absolute;
  top: calc(100% + ${spacing.x1}px);
  left: 0;
  right: 0;
  max-height: 240px;
  overflow-y: auto;
  background-color: ${colors.background};
  border-radius: ${borderRadius.regular}px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 2;
`;

const StoreMenuItem = styled.button<{ $selected: boolean }>`
  display: block;
  width: 100%;
  padding: ${spacing.x2}px ${spacing.x2}px;
  border: none;
  background: none;
  color: ${({ $selected }) => ($selected ? colors.primary : colors.text)};
  font-size: ${fontSizes.small}px;
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
  text-align: left;
  cursor: pointer;

  &:hover {
    background-color: ${colors.backgroundDark};
  }
`;

const storeLabel = (store: RestStore) => store.displayName || store.name;

type SidebarContextHeaderProps = {
  resellerId?: ResellerId | null;
  chainId?: ChainId | null;
  storeId?: StoreId | null;
  onNavigate?: () => void;
};

export const SidebarContextHeader = ({
  resellerId,
  chainId,
  storeId,
  onNavigate,
}: SidebarContextHeaderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const { data: store } = useGetStoreQuery(storeId ?? undefined);
  const resolvedChainId = chainId ?? store?.chainId;
  const chainQuery = useGetChainQuery(resolvedChainId);
  const chain = chainQuery.data;
  const resolvedResellerId = resellerId
    ? resellerId
    : store
      ? store.resellerId ?? undefined
      : chain?.resellerId ?? undefined;
  const { data: reseller } = useGetResellerQuery(resolvedResellerId ?? undefined);
  const { data: user, isFetched: userFetched } = useGetUserQuery();
  const accessReady = userFetched && (!resolvedChainId || chainQuery.isFetched);
  const isAdmin = user?.role === UserRole.ADMIN;
  const isResellerForChain =
    user?.role === UserRole.RESELLER_ADMIN &&
    !!user.resellerId &&
    !!chain?.resellerId &&
    user.resellerId === chain.resellerId;
  const hasChainMembership =
    !!resolvedChainId && (user?.chainIds?.includes(resolvedChainId) ?? false);
  const canListChainStores =
    accessReady &&
    !!resolvedChainId &&
    (isAdmin || isResellerForChain || hasChainMembership);
  const restrictToUserStores =
    accessReady && !!resolvedChainId && !canListChainStores;
  const { data: chainStores } = useGetChainStoresQuery(
    resolvedChainId,
    canListChainStores,
  );
  const { data: userStores } = useGetStoresQuery();
  const listedStores = canListChainStores
    ? chainStores
    : restrictToUserStores
      ? userStores?.filter((item) => item.chainId === resolvedChainId)
      : undefined;
  const stores = listedStores ?? (store ? [store] : []);
  const selectedStore =
    stores.find((item) => item.id === storeId) ?? store ?? null;
  const canOpenReseller =
    !!reseller &&
    (user?.role === UserRole.ADMIN ||
      (user?.role === UserRole.RESELLER_ADMIN &&
        user.resellerId === reseller.id));

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (!target.closest("[data-context-store-menu]")) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [menuOpen]);

  const openStore = (nextStoreId: StoreId) => {
    localStorage.setItem("selectedStoreId", nextStoreId);
    setMenuOpen(false);
    onNavigate?.();
    navigate(`/stores/${nextStoreId}/overview`);
  };

  if (!reseller && !chain && stores.length === 0) {
    return null;
  }

  return (
    <ContextHeader>
      {reseller && (
        <div>
          <ContextLabel>{t("resellerSidebar.sectionTitle")}</ContextLabel>
          {canOpenReseller ? (
            <ContextLink
              to={`/resellers/${reseller.id}/overview`}
              onClick={() => onNavigate?.()}
            >
              {reseller.name}
            </ContextLink>
          ) : (
            <ContextName>{reseller.name}</ContextName>
          )}
        </div>
      )}
      {chain && (
        <div>
          <ContextLabel>{t("chainSidebar.sectionTitle")}</ContextLabel>
          <ContextLink
            to={`/chains/${chain.id}/overview`}
            onClick={() => onNavigate?.()}
          >
            {chain.name}
          </ContextLink>
        </div>
      )}
      {stores.length > 1 && (
        <StoreMenu data-context-store-menu>
          <ContextLabel>{t("sidebar.store")}</ContextLabel>
          <StoreMenuTrigger
            type="button"
            aria-expanded={menuOpen}
            aria-haspopup="listbox"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span>
              {selectedStore
                ? storeLabel(selectedStore)
                : t("storeDropdown.selectStore")}
            </span>
            <DropdownArrow $isOpen={menuOpen} />
          </StoreMenuTrigger>
          {menuOpen && (
            <StoreMenuList role="listbox">
              {stores.map((item) => (
                <StoreMenuItem
                  key={item.id}
                  type="button"
                  role="option"
                  aria-selected={item.id === storeId}
                  $selected={item.id === storeId}
                  onClick={() => openStore(item.id)}
                >
                  {storeLabel(item)}
                </StoreMenuItem>
              ))}
            </StoreMenuList>
          )}
        </StoreMenu>
      )}
      {stores.length === 1 && (
        <div>
          <ContextLabel>{t("sidebar.store")}</ContextLabel>
          <ContextLink
            to={`/stores/${stores[0].id}/overview`}
            onClick={() => {
              localStorage.setItem("selectedStoreId", stores[0].id);
              onNavigate?.();
            }}
          >
            {storeLabel(stores[0])}
          </ContextLink>
        </div>
      )}
    </ContextHeader>
  );
};

